import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { Eyebrow, Reveal } from "../components/Reveal";
import { ArrowRight, CalendarDays } from "lucide-react";

import API from "../api";
const SITE = "https://www.collanmoreislandlodge.com";

const SEO = {
  title: "Island Journal | Private Island Breaks & Group Trips Westport",
  description:
    "Guides from Collanmore Island Lodge: group accommodation in Westport, hen and stag weekends, corporate retreats, kayaking in Clew Bay and Traitors Island.",
  path: "/journal/",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Collanmore Island Lodge — Island Journal",
      url: `${SITE}/journal/`,
    },
  ],
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })
    : "";

export default function JournalPage() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/journal`)
      .then((r) => r.json())
      .then((d) => alive && setPosts(d.posts || []))
      .catch(() => alive && setPosts([]));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <PageLayout seo={SEO}>
      <main className="bg-navy pb-24 pt-36 md:pt-44" data-testid="journal-page">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Reveal>
            <Eyebrow light>Island Journal</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-tight text-ivory md:text-6xl" data-testid="journal-heading">
              Notes from the island — guides for groups who’d rather do something different
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-sm font-light leading-relaxed text-ivory/75 md:text-base">
              Planning a hen weekend, a corporate retreat, a family reunion or simply curious about
              life on Clew Bay? Practical guides and island stories from the Collanmore team.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="journal-grid">
            {(posts || []).map((post, i) => (
              <Reveal key={post.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/journal/${post.slug}/`}
                  data-testid={`journal-article-${i + 1}`}
                  className="group flex h-full flex-col border border-ivory/15 bg-navy-2 transition-colors duration-500 hover:border-gold/50"
                >
                  <div className="aspect-[3/2] w-full overflow-hidden bg-navy">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-navy-2 to-navy" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest2 text-gold-2">
                      <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(post.created_at)}
                    </p>
                    <h2 className="mt-3 font-display text-xl leading-snug text-ivory">{post.title}</h2>
                    <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-ivory/65">{post.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest2 text-gold-2">
                      Read the article <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {posts && posts.length === 0 && (
            <p className="mt-16 text-center text-xs uppercase tracking-widest2 text-ivory/50" data-testid="journal-empty">
              The first entries are being written — check back soon.
            </p>
          )}
        </div>
      </main>
    </PageLayout>
  );
}